import React from 'react';
import Icon from '../../../components/AppIcon';

const NotificationsFeed = ({ notifications }) => {
  const getNotificationIcon = (type) => {
    const icons = {
      approval: 'CheckCircle',
      rejection: 'XCircle',
      campaign: 'Megaphone',
      score: 'TrendingUp',
      payout: 'DollarSign',
      system: 'Bell'
    };
    return icons?.[type] || 'Bell';
  };

  const getNotificationColor = (type) => {
    const colors = {
      approval: 'text-emerald-500 bg-emerald-500/10',
      rejection: 'text-red-500 bg-red-500/10',
      campaign: 'text-blue-500 bg-blue-500/10',
      score: 'text-amber-500 bg-amber-500/10',
      payout: 'text-teal-500 bg-teal-500/10',
      system: 'text-muted-foreground bg-muted'
    };
    return colors?.[type] || 'text-muted-foreground bg-muted';
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const notifDate = new Date(timestamp);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return notifDate?.toLocaleDateString();
  };

  return (
    <div className="bg-card rounded-xl p-6 md:p-8 border border-border">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">Recent Activity</h2>
          <p className="text-sm text-muted-foreground">Latest updates and notifications</p>
        </div>
        <button className="w-10 h-10 rounded-lg bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors">
          <Icon name="Settings" size={18} color="var(--color-foreground)" />
        </button>
      </div>
      <div className="space-y-4 max-h-[600px] overflow-y-auto scrollbar-custom">
        {notifications?.map((notification) => (
          <div
            key={notification?.id}
            className={`p-4 rounded-lg border transition-all duration-250 hover:shadow-md ${
              notification?.unread
                ? 'bg-primary/5 border-primary/20' :'bg-muted/30 border-border'
            }`}
          >
            <div className="flex gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getNotificationColor(notification?.type)}`}>
                <Icon name={getNotificationIcon(notification?.type)} size={18} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="text-sm font-semibold text-foreground line-clamp-1">
                    {notification?.title}
                  </h3>
                  {notification?.unread && (
                    <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                  )}
                </div>
                
                <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                  {notification?.message}
                </p>
                
                <div className="flex items-center gap-4">
                  <span className="text-xs text-muted-foreground">
                    {formatTimestamp(notification?.timestamp)}
                  </span>
                  {notification?.actionLabel && (
                    <button className="text-xs font-medium text-primary hover:text-primary/80 transition-colors">
                      {notification?.actionLabel}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {notifications?.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
            <Icon name="Bell" size={32} color="var(--color-muted-foreground)" />
          </div>
          <p className="text-muted-foreground">No notifications yet</p>
        </div>
      )}
    </div>
  );
};

export default NotificationsFeed;